import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  viewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { take } from 'rxjs/operators';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import {
  AlarmLimit,
  AlarmLimitSortOrQuery,
} from 'src/app/model/operational-monitoring/alarm-limit';
import { AlarmLimitsTableService } from 'src/app/services/table/alarm-limits-table.service';
import { getAlarmLimitsColumns } from '../../definitions/alarm-limits-table-definition';
import { Subscription, forkJoin, interval } from 'rxjs';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { TabService } from 'src/app/core/services/tab.service';
import { OperationalMonitoringApiService } from 'src/app/services/api/operational-monitoring-api.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { AlarmLimitsDetailContainerComponent } from '../alarm-limits-detail-container/alarm-limits-detail-container.component';
import { AlarmLimitsBuilderService } from 'src/app/services/builder/alarm-limits-builder.service';
import { BusinessUnitFilterComponent } from 'src/app/core/components/business-unit-filter/business-unit-filter.component';
import { RequestAlarmMonitoringComponent } from '../../components/request-alarm-monitoring/request-alarm-monitoring.component';
import { SYNC_ALARM_LIMITS_CONTAINER } from 'src/app/constants/common.constants';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { BusinessUnitFilterComponent as BusinessUnitFilterComponent_1 } from '../../../core/components/business-unit-filter/business-unit-filter.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';

@Component({
  selector: 'app-alarm-limits-search-container',
  templateUrl: './alarm-limits-search-container.component.html',
  styleUrls: ['./alarm-limits-search-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    ProgressSpinnerComponent,
    BusinessUnitFilterComponent_1,
    ButtonDirective,
    GeneralGridComponent,
    AlarmLimitsDetailContainerComponent,
    RequestAlarmMonitoringComponent,
    TranslatePipe,
  ],
})
export class AlarmLimitsSearchContainerComponent
  extends SearchContainerComponent<AlarmLimit>
  implements OnInit, OnDestroy, AfterViewInit
{
  protected _authApi = inject(AuthApiService);
  protected _tableService = inject(AlarmLimitsTableService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _translateService = inject(TranslateService);
  protected _opMonitoringApi = inject(OperationalMonitoringApiService);
  protected _alarmLimitBuilder = inject(AlarmLimitsBuilderService);
  protected _tabService = inject(TabService);

  readonly alarmLimitForm =
    viewChild.required<AlarmLimitsDetailContainerComponent>('alarmLimitForm');
  readonly businessUnitFilter =
    viewChild<BusinessUnitFilterComponent>('buFilter');
  readonly alarmMonitoringRequest =
    viewChild<RequestAlarmMonitoringComponent>('requestAM');

  displayDialog: boolean = false;
  displayRequestDialog: boolean = false;
  dialogHeader: string = '';
  refreshSubscription?: Subscription;
  activeTabChangedSub?: Subscription;
  formLoading: boolean = true;
  requestLoading: boolean = true;

  alarmLimits: AlarmLimit[] = [];
  formType: string = 'Create';
  showSuccessMessage = true;
  selectedAlarmLimit: AlarmLimit | null = null;

  canCreate = false;
  canSync = false;

  constructor() {
    super();
  }

  ngOnInit() {
    this.canCreate = this._authApi.doesUserHaveAtLeastOneClaimFromList([
      CLAIMS.FIELD_OPERATIONS.ALARM_LIMITS.CHANGE_ALL_ALARM_LIMITS,
      CLAIMS.FIELD_OPERATIONS.ALARM_LIMITS.CHANGE_DIRECT_REPORTS_ALARM_LIMITS,
      CLAIMS.FIELD_OPERATIONS.ALARM_LIMITS.CHANGE_OWN_ALARM_LIMITS,
    ]);
    this.canSync = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.FIELD_OPERATIONS.ALARM_LIMITS.CHANGE_ALL_ALARM_LIMITS,
    ]);
    this.setTableService(this._tableService);
    this.setupActiveTabChangedSubscription();
    this.columns = getAlarmLimitsColumns();

    const screenName$ = this._translateService.get(
      'OPMONITORING.SCREEN.ALARM_LIMITS'
    );
    const dialogHeader$ = this._translateService.get(
      'OPMONITORING.SCREEN.ALARM_LIMITS'
    );
    forkJoin([screenName$, dialogHeader$]).subscribe((label) => {
      this._tabService.updateActiveTabLabel(label[0]);
      this.screenName = label[0];
      this.dialogHeader = label[1];
    });

    const refreshInterval = interval(300000);
    this.refreshSubscription = refreshInterval.subscribe(() => {
      this.refresh(false);
    });
  }

  ngAfterViewInit(): void {
    this.applyPreviousData();
    this.applyPreviousFilters();
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ): void {
    let selectedBusinessUnits = [];

    const businessUnitFilter = this.businessUnitFilter();
    if (businessUnitFilter?.selectedBusinessUnits) {
      selectedBusinessUnits = businessUnitFilter.selectedBusinessUnits;
    } else {
      selectedBusinessUnits = this._authApi
        .getUserBusinessUnits()
        .map((bu) => bu.id);
    }

    filters['businessUnits'] = selectedBusinessUnits as any;
    const query = this._alarmLimitBuilder.buildAlarmLimitQuery(filters);
    this.queryNetwork(req, $event, query);
  }

  private queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ): void {
    req.page += 1;
    const sort = this.buildNetworkSortQuery(
      $event,
      'effectiveDateTime',
      'DESC'
    );

    this._opMonitoringApi
      .queryForAlarmLimits(
        req.pageSize,
        req.page,
        query as AlarmLimitSortOrQuery,
        sort
      )
      .subscribe({
        next: (alarmLimitConnection) => {
          this.totalRecords = alarmLimitConnection.totalRecords;
          this.alarmLimits = alarmLimitConnection.items;
          this.elements = [...this.alarmLimits];
        },
        error: (error) => {
          this.loading = false;
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'OPMONITORING.MESSAGES.ERROR.QUERY_ALARM_LIMITS',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
        },
        complete: () => {
          this.loading = false;
          if (this.showSuccessMessage) {
            this.showSuccessMessage = false;
            this._logAndMessage.translateToSuccessMessage({
              bodyKey: 'OPMONITORING.MESSAGES.SUCCESS.QUERY_ALARM_LIMITS',
              headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
            });
          }
        },
      });
  }

  handleBusinessUnitChange() {
    this.lazyLoad({ lazy: this._tableService.getLastLazyLoad()! });
  }

  changeViewEvent($event: { data: AlarmLimit }) {
    this.formType = 'Edit';
    this.selectedAlarmLimit = $event.data;

    setTimeout(() => {
      this.alarmLimitForm().updateFields();
      this.displayDialog = true;
    }, 5);
  }

  refresh(setLoading = true): void {
    this.lazyLoad({ lazy: this._tableService.getLastLazyLoad()! }, setLoading);
  }

  setupActiveTabChangedSubscription(): void {
    this.activeTabChangedSub = this._tabService.activeTabChanged.subscribe(
      (tab) => {
        if (!isNullOrUndefined(tab) && tab.header === this.screenName) {
          this._tableService.setLastLazyLoad(
            this._tableService.getLastLazyLoad()
          );
          this.refresh();
        }
      }
    );
  }

  newAlarmLimit() {
    this.formType = 'Create';
    this.alarmLimitForm().resetEffectiveDate();
    this.alarmLimitForm().enableForm();
    this.displayDialog = true;
  }

  onFormHidden() {
    this.displayDialog = false;
  }

  onFormSubmitted() {
    this.refresh();
  }

  onAlarmDeleted() {
    this.refresh();
  }

  onPreparedForm() {
    this.formLoading = false;
  }

  requestAlarmMonitoring() {
    this.displayRequestDialog = true;
  }

  onRequestHidden() {
    this.displayRequestDialog = false;
  }

  onPreparedRequest() {
    this.requestLoading = false;
  }

  syncAlarmLimits() {
    const tab = this._tabService.buildNewTab(SYNC_ALARM_LIMITS_CONTAINER, true);
    this._tabService.openTab(tab);
  }

  ngOnDestroy(): void {
    this.refreshSubscription?.unsubscribe();
  }
}
